import { useState } from "react";
import { motion } from "framer-motion";
import { Clock, Instagram, MapPin, MessageCircle } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { storeAmbienceImage } from "@/data/products";
import { IG_HANDLE, IG_LINK, WA_LINK } from "@/i18n";

const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
const BUSY = {
  fri: [25, 35, 45, 40, 35, 50, 60, 75, 95, 100, 85, 60, 35],
  sun: [45, 65, 90, 100, 95, 80, 65, 55, 45, 40, 30, 20, 15],
};

function BusyChart() {
  const { t } = useStore();
  const [day, setDay] = useState("fri");
  const data = BUSY[day];
  const peak = Math.max(...data);

  return (
    <div
      data-testid="busy-hours-chart"
      className="rounded-3xl border border-white/10 bg-ink-card p-6 lg:p-8"
    >
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="font-display text-lg font-bold text-white lg:text-xl">
            {t.location.busyTitle}
          </h3>
          <p className="mt-1 text-sm text-mist">{t.location.busySubtitle}</p>
        </div>
        <div className="flex rounded-full border border-white/15 p-0.5" data-testid="busy-day-toggle">
          {["fri", "sun"].map((d) => (
            <button
              key={d}
              data-testid={`busy-day-${d}`}
              onClick={() => setDay(d)}
              className={`rounded-full px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 ${
                day === d ? "bg-ember text-black" : "text-mist hover:text-white"
              }`}
            >
              {t.location.days[d]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex h-44 items-end gap-1.5 sm:gap-2.5">
        {data.map((v, i) => (
          <div key={i} className="group flex h-full flex-1 flex-col items-center justify-end gap-2">
            <span
              className={`font-mono text-[9px] transition-opacity duration-300 ${
                v === peak ? "text-ember opacity-100" : "text-zinc-500 opacity-0 group-hover:opacity-100"
              }`}
            >
              {v === peak ? t.location.peak : `${v}%`}
            </span>
            <motion.div
              data-testid={v === peak ? "busy-bar-peak" : undefined}
              initial={{ height: 0 }}
              animate={{ height: `${v}%` }}
              transition={{ duration: 0.7, delay: i * 0.03, ease: [0.16, 1, 0.3, 1] }}
              className={`w-full rounded-t-md ${
                v === peak
                  ? "bg-gradient-to-t from-ember to-ember-light shadow-[0_0_20px_rgba(255,107,0,0.35)]"
                  : "bg-zinc-700/60 group-hover:bg-zinc-600"
              }`}
            />
            <span className="font-mono text-[9px] text-zinc-500">
              {String(HOURS[i]).padStart(2, "0")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LocationSection() {
  const { t } = useStore();

  const rows = [
    { icon: MapPin, label: null, value: t.location.address, testId: "store-address" },
    { icon: Clock, label: t.location.hoursLabel, value: t.location.hours, testId: "store-hours" },
    { icon: Instagram, label: t.location.instagramLabel, value: `@${IG_HANDLE}`, href: IG_LINK, testId: "store-instagram" },
  ];

  return (
    <section id="lokasi" data-testid="location-section" className="relative overflow-hidden bg-black py-20 lg:py-28">
      <div className="pointer-events-none absolute -left-40 bottom-0 h-[26rem] w-[26rem] rounded-full bg-ember/10 blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-12"
        >
          <div className="mb-4 flex items-center gap-3">
            <span className="h-px w-10 bg-ember" />
            <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-ember">
              {t.location.eyebrow}
            </span>
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            {t.location.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-12 lg:gap-8">
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative flex flex-col justify-between overflow-hidden rounded-3xl border border-white/10 bg-ink-card lg:col-span-5"
          >
            <div className="relative h-40 overflow-hidden">
              <img
                src={storeAmbienceImage}
                alt="Puppy Nusantara Store ambience"
                className="h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-card to-transparent" />
            </div>
            <div className="flex flex-1 flex-col gap-6 p-8">
              {rows.map((r) => (
                <div key={r.testId} className="flex items-start gap-4" data-testid={r.testId}>
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-ember/30 bg-ember/10 text-ember">
                    <r.icon size={15} />
                  </span>
                  <div>
                    {r.label && (
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                        {r.label}
                      </p>
                    )}
                    {r.href ? (
                      <a
                        href={r.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-0.5 block text-sm font-semibold text-white transition-colors hover:text-ember"
                      >
                        {r.value}
                      </a>
                    ) : (
                      <p className="mt-0.5 text-sm font-semibold leading-relaxed text-white">
                        {r.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              <a
                data-testid="location-whatsapp-btn"
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex items-center justify-center gap-3 rounded-full bg-ember px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition-all duration-300 hover:bg-ember-light hover:shadow-[0_0_32px_rgba(255,107,0,0.4)]"
              >
                <MessageCircle size={15} />
                {t.location.whatsappCta}
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col gap-6 lg:col-span-7"
          >
            <div
              data-cursor="hide"
              className="relative min-h-[320px] flex-1 overflow-hidden rounded-3xl border border-white/10 lg:min-h-[380px]"
            >
              <iframe
                data-testid="google-map-iframe"
                title="Puppy Nusantara Store — PMQX+H7 Pd. Kacang Tim., Kota Tangerang Selatan, Banten"
                src="https://www.google.com/maps?q=PMQX%2BH7+Pondok+Kacang+Timur,+Tangerang+Selatan,+Banten&z=16&output=embed"
                className="absolute inset-0 h-full w-full"
                style={{ border: 0, filter: "grayscale(0.3) invert(0.9) hue-rotate(180deg) contrast(0.9)" }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <BusyChart />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
